import { useState, useMemo, useEffect } from "react"
import { Navigation } from "@/components/Navigation"
import { SEO } from "@/components/SEO"
import { Footer } from "@/components/Footer"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { toast } from "sonner"
import { Upload, CheckCircle, ArrowLeft, FileText, Bold, Italic, List, AlignLeft, Paperclip } from "lucide-react"
import { Link } from "react-router-dom"

const JobApplication = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [fileName, setFileName] = useState<string | null>(null)
  const [workAuth, setWorkAuth] = useState<string>("")
  const [resumeFile, setResumeFile] = useState<File | null>(null)

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    educationSummary: "",
    currentPosition: "",
    securityClearance: "",
    visaType: "",
    availability: "",
    relocation: "",
    willingToTravel: "",
    currency: "USD",
    expectedCompensation: "",
    compensationFrequency: "",
    totalExperience: "",
    industry: "",
    certifications: "",
    preferredWorkLocation: "",
    country: "",
    state: "",
    city: "",
    zipCode: "",
    linkedinUrl: "",
    githubUrl: "",
    coverLetter: "",
    skills: "",
  })

  // Update form field handler
  const updateFormField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  // Validation: Check if all mandatory fields are filled
  const isFormValid = useMemo(() => {
    const mandatoryFields = [
      formData.firstName,
      formData.lastName,
      formData.email,
      formData.linkedinUrl,
      workAuth,
      formData.availability,
      formData.skills,
      formData.educationSummary,
      formData.currentPosition,
      formData.totalExperience,
      formData.coverLetter,
    ]

    // Check if all mandatory fields have values
    return mandatoryFields.every(field => field && field.toString().trim() !== '')
  }, [formData, workAuth])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Get form element
      const form = e.currentTarget

      // Read values directly from form inputs
      const firstName = (form.elements.namedItem('firstName') as HTMLInputElement)?.value || ''
      const lastName = (form.elements.namedItem('lastName') as HTMLInputElement)?.value || ''
      const email = (form.elements.namedItem('email') as HTMLInputElement)?.value || ''
      const phone = (form.elements.namedItem('phone') as HTMLInputElement)?.value || ''

      // Read all other text inputs directly from form
      const educationSummary = (form.elements.namedItem('educationSummary') as HTMLInputElement)?.value || ''
      const currentPosition = (form.elements.namedItem('currentPosition') as HTMLInputElement)?.value || ''
      const expectedCompensation = (form.elements.namedItem('expectedCompensation') as HTMLInputElement)?.value || ''
      const totalExperience = (form.elements.namedItem('totalExperience') as HTMLInputElement)?.value || ''
      const certifications = (form.elements.namedItem('certifications') as HTMLInputElement)?.value || ''
      const preferredWorkLocation = (form.elements.namedItem('preferredWorkLocation') as HTMLInputElement)?.value || ''
      const state = (form.elements.namedItem('state') as HTMLInputElement)?.value || ''
      const city = (form.elements.namedItem('city') as HTMLInputElement)?.value || ''
      const zipCode = (form.elements.namedItem('zipCode') as HTMLInputElement)?.value || ''
      const linkedinUrl = (form.elements.namedItem('linkedinUrl') as HTMLInputElement)?.value || ''
      const githubUrl = (form.elements.namedItem('githubUrl') as HTMLInputElement)?.value || ''
      const coverLetter = (form.elements.namedItem('coverLetter') as HTMLTextAreaElement)?.value || ''
      const skills = (form.elements.namedItem('skills') as HTMLInputElement)?.value || ''

      // Prepare JSON payload with values object (matching the API format)
      const payload = {
        values: {
          'First Name': firstName,
          'Last Name': lastName,
          'Email': email,
          'Phone': phone,
          'Education Summary': educationSummary,
          'Current Position': currentPosition,
          'Security Clearance': formData.securityClearance || '',
          'Work Authorization': workAuth || '',
          'VISA TYPE': formData.visaType || '',
          'Availability': formData.availability || '',
          'Open to Relocation?': formData.relocation || '',
          'Willing to travel': formData.willingToTravel || '',
          'Currency': formData.currency || 'USD',
          'Expected Compensation': expectedCompensation,
          'Compensation Frequency ': formData.compensationFrequency || '',
          'Total Experience': totalExperience,
          'Industry': formData.industry || '',
          'Certifications': certifications,
          'Preferred Work Location': preferredWorkLocation,
          'Country': formData.country || '',
          'State / Province': state,
          'City': city,
          'ZIP / Postal Code': zipCode,
          'LinkedIn URL': linkedinUrl,
          'GitHub / Portfolio URL': githubUrl,
          'Cover Letter': coverLetter,
          'Skills': skills,
        }
      }

      // Use the public ingest endpoint - no authentication needed
      const INGEST_URL = import.meta.env.VITE_UPLOAD_ENDPOINT || 'https://api.leorix.com/api/v2/templates/ingest/Gj1xs8nD1y1tqkcsjDz-d5XW9A2M2o6XPnbBOAiY/'

      if (!INGEST_URL || INGEST_URL.includes('undefined')) {
        throw new Error('API endpoint is not configured properly. Please check environment variables.')
      }

      // Make API call - send as JSON with proper Content-Type header
      const response = await fetch(INGEST_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const responseText = await response.text()

      if (!response.ok) {
        let errorMessage = 'Application submission failed'
        try {
          const errorData = JSON.parse(responseText)
          errorMessage = errorData.message || errorMessage
        } catch {
          errorMessage = responseText || errorMessage
        }
        throw new Error(errorMessage)
      }

      setIsSuccess(true)
      toast.success("Application submitted successfully!")
    } catch (error) {
      console.error('Submission error:', error)
      toast.error(error instanceof Error ? error.message : "Failed to submit application. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (
        file.type === "application/pdf" ||
        file.type === "application/msword" ||
        file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ) {
        setFileName(file.name)
        setResumeFile(file)
      } else {
        toast.error("Please upload a PDF or Doc file.")
        e.target.value = "" // Reset input
      }
    }
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="pt-32 pb-20 section-container min-h-[80vh] flex flex-col items-center justify-center text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-8 animate-in zoom-in duration-500">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold mb-4 font-heading">
            Application Received
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mb-12">
            Thank you for your interest in joining RSA Tech Group. Our talent
            acquisition team will review your profile and get back to you
            shortly.
          </p>
          <Link to="/careers">
            <Button className="bg-black text-white hover:bg-gray-800">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Careers
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    )
  }

  return (

    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30 font-sans selection:bg-blue-100">
      <SEO
        title="Job Application | Join the Intelligence Revolution | RSA Tech Group"
        description="Apply to be part of the team architecting the neural infrastructure of tomorrow. Join RSA Tech Group's intelligence revolution."
      />
      <Navigation />

      {/* Decorative Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/40 rounded-full blur-3xl opacity-50 translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-100/40 rounded-full blur-3xl opacity-50 -translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="pt-32 pb-24 px-4 md:px-8 max-w-[1600px] mx-auto relative z-10">
        <div className="mb-8 flex items-center justify-between">
          <Link to="/careers" className="group flex items-center gap-2 text-gray-500 hover:text-blue-600 transition-colors duration-300">
            <div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center group-hover:border-blue-200 group-hover:bg-blue-50 transition-all">
              <ArrowLeft className="w-4 h-4" />
            </div>
            <span className="font-medium text-sm">Back to Careers</span>
          </Link>
        </div>

        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold font-heading mb-4 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600">
            Apply Now
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl">
            Take the next step in your career. Join us in building the future of autonomous enterprise.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* LEFT COLUMN - Profile Form */}
          <div className="lg:col-span-8 space-y-8">
            <div className="bg-white/70 backdrop-blur-xl p-8 md:p-10 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 opacity-80" />

              <div className="mb-8 pb-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 font-heading">Candidate Profile</h2>
                  <p className="text-sm text-gray-500 mt-1">Please fill in your personal information accurately.</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-blue-600 opacity-50" />
                </div>
              </div>

              <div className="space-y-8">
                {/* Name & Email Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">First Name *</Label>
                    <Input
                      name="firstName"
                      required
                      defaultValue={formData.firstName}
                      onChange={(e) => {
                        const value = e.target.value
                        updateFormField('firstName', value)
                      }}
                      className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Last Name *</Label>
                    <Input
                      name="lastName"
                      required
                      defaultValue={formData.lastName}
                      onChange={(e) => {
                        const value = e.target.value
                        updateFormField('lastName', value)
                      }}
                      className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Email *</Label>
                    <Input
                      name="email"
                      type="email"
                      required
                      defaultValue={formData.email}
                      onChange={(e) => {
                        const value = e.target.value
                        updateFormField('email', value)
                      }}
                      className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                    />
                  </div>
                </div>

                {/* Phone, Education, Current Pos */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Phone *</Label>
                    <Input
                      name="phone"
                      type="tel"
                      required
                      defaultValue={formData.phone}
                      onChange={(e) => updateFormField('phone', e.target.value)}
                      className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                      placeholder="(555) 000-0000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Education Summary *</Label>
                    <Input
                      name="educationSummary"
                      required
                      defaultValue={formData.educationSummary}
                      onChange={(e) => updateFormField('educationSummary', e.target.value)}
                      className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                      placeholder="e.g. MS in CS"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Current Position *</Label>
                    <Input
                      name="currentPosition"
                      required
                      defaultValue={formData.currentPosition}
                      onChange={(e) => updateFormField('currentPosition', e.target.value)}
                      className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                    />
                  </div>
                </div>

                {/* Eligibility & Availability Header */}
                <div className="pt-6 border-t border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <span className="w-1 h-6 bg-blue-500 rounded-full inline-block"></span>
                    Eligibility & Availability
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Security Clearance</Label>
                      <Select value={formData.securityClearance} onValueChange={(value) => updateFormField('securityClearance', value)}>
                        <SelectTrigger className="bg-white/50 border-gray-200 focus:ring-4 focus:ring-blue-500/10 rounded-xl h-12"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="Active Secret">Active Secret</SelectItem>
                          <SelectItem value="Top Secret">Top Secret</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Work Authorization *</Label>
                      <Select required value={workAuth} onValueChange={(value) => setWorkAuth(value)}>
                        <SelectTrigger className="bg-white/50 border-gray-200 focus:ring-4 focus:ring-blue-500/10 rounded-xl h-12"><SelectValue placeholder="Select Status..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="US Citizen">US Citizen</SelectItem>
                          <SelectItem value="Green Card">Green Card</SelectItem>
                          <SelectItem value="Visa Sponsorship">Visa Sponsorship</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {workAuth === "Visa Sponsorship" && (
                      <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                        <Label className="uppercase text-[11px] font-bold text-blue-500 tracking-widest pl-1">Visa Type</Label>
                        <Select value={formData.visaType} onValueChange={(value) => updateFormField('visaType', value)}>
                          <SelectTrigger className="bg-blue-50/50 border-blue-200 focus:ring-4 focus:ring-blue-500/10 rounded-xl h-12"><SelectValue placeholder="Select Visa Type..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="H1B">H1B</SelectItem>
                            <SelectItem value="OPT">OPT</SelectItem>
                            <SelectItem value="EAD">EAD</SelectItem>
                            <SelectItem value="CPT">CPT</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Availability *</Label>
                      <Select required value={formData.availability} onValueChange={(value) => updateFormField('availability', value)}>
                        <SelectTrigger className="bg-white/50 border-gray-200 focus:ring-4 focus:ring-blue-500/10 rounded-xl h-12"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Immediate">Immediate</SelectItem>
                          <SelectItem value="2 Weeks">2 Weeks</SelectItem>
                          <SelectItem value="1 Month">1 Month</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div className="space-y-2">
                      <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Open to Relocation?</Label>
                      <Select value={formData.relocation} onValueChange={(value) => updateFormField('relocation', value)}>
                        <SelectTrigger className="bg-white/50 border-gray-200 focus:ring-4 focus:ring-blue-500/10 rounded-xl h-12"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Willing to Travel</Label>
                      <Select value={formData.willingToTravel} onValueChange={(value) => updateFormField('willingToTravel', value)}>
                        <SelectTrigger className="bg-white/50 border-gray-200 focus:ring-4 focus:ring-blue-500/10 rounded-xl h-12"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent><SelectItem value="0%">0%</SelectItem><SelectItem value="Up to 25%">Up to 25%</SelectItem><SelectItem value="Up to 50%">Up to 50%</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Compensation Header */}
                <div className="pt-6 border-t border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <span className="w-1 h-6 bg-purple-500 rounded-full inline-block"></span>
                    Compensation
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Currency</Label>
                      <Select value={formData.currency} onValueChange={(value) => updateFormField('currency', value)}>
                        <SelectTrigger className="bg-white/50 border-gray-200 focus:ring-4 focus:ring-blue-500/10 rounded-xl h-12"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD - US Dollar</SelectItem>
                          <SelectItem value="EUR">EUR - Euro</SelectItem>
                          <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Expected Compensation</Label>
                      <Input
                        name="expectedCompensation"
                        type="number"
                        defaultValue={formData.expectedCompensation}
                        onChange={(e) => updateFormField('expectedCompensation', e.target.value)}
                        className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Frequency</Label>
                      <Select value={formData.compensationFrequency} onValueChange={(value) => updateFormField('compensationFrequency', value)}>
                        <SelectTrigger className="bg-white/50 border-gray-200 focus:ring-4 focus:ring-blue-500/10 rounded-xl h-12"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent><SelectItem value="Per Year">Per Year</SelectItem><SelectItem value="Per Hour">Per Hour</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Experience & Industry */}
                <div className="pt-6 border-t border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <span className="w-1 h-6 bg-pink-500 rounded-full inline-block"></span>
                    Experience & Industry
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="space-y-2">
                      <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Total Experience (Years) *</Label>
                      <Input
                        name="totalExperience"
                        type="number"
                        required
                        defaultValue={formData.totalExperience}
                        onChange={(e) => updateFormField('totalExperience', e.target.value)}
                        className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Industry</Label>
                      <Select value={formData.industry} onValueChange={(value) => updateFormField('industry', value)}>
                        <SelectTrigger className="bg-white/50 border-gray-200 focus:ring-4 focus:ring-blue-500/10 rounded-xl h-12"><SelectValue placeholder="Select Industry..." /></SelectTrigger>
                        <SelectContent><SelectItem value="Technology / Software">Technology / Software</SelectItem><SelectItem value="Finance">Finance</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Enter Certifications (comma-separated)</Label>
                    <Input
                      name="certifications"
                      defaultValue={formData.certifications}
                      onChange={(e) => updateFormField('certifications', e.target.value)}
                      className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                      placeholder="e.g. AWS Certified Solutions Architect, PMP"
                    />
                  </div>
                </div>


                {/* Location */}
                <div className="pt-6 border-t border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <span className="w-1 h-6 bg-green-500 rounded-full inline-block"></span>
                    Location
                  </h3>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Preferred Work Location</Label>
                        <Input
                          name="preferredWorkLocation"
                          defaultValue={formData.preferredWorkLocation}
                          onChange={(e) => updateFormField('preferredWorkLocation', e.target.value)}
                          className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                          placeholder="Anywhere"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Country</Label>
                        <Select value={formData.country} onValueChange={(value) => updateFormField('country', value)}>
                          <SelectTrigger className="bg-white/50 border-gray-200 focus:ring-4 focus:ring-blue-500/10 rounded-xl h-12"><SelectValue placeholder="Select..." /></SelectTrigger>
                          <SelectContent><SelectItem value="United States">United States</SelectItem><SelectItem value="United Kingdom">United Kingdom</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">State / Province</Label>
                        <Input
                          name="state"
                          defaultValue={formData.state}
                          onChange={(e) => updateFormField('state', e.target.value)}
                          className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">City</Label>
                        <Input
                          name="city"
                          defaultValue={formData.city}
                          onChange={(e) => updateFormField('city', e.target.value)}
                          className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Zip / Postal Code</Label>
                        <Input
                          name="zipCode"
                          defaultValue={formData.zipCode}
                          onChange={(e) => updateFormField('zipCode', e.target.value)}
                          className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Links */}
                <div className="pt-6 border-t border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <span className="w-1 h-6 bg-orange-500 rounded-full inline-block"></span>
                    Professional Links
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">LinkedIn URL *</Label>
                      <Input
                        name="linkedinUrl"
                        required
                        defaultValue={formData.linkedinUrl}
                        onChange={(e) => updateFormField('linkedinUrl', e.target.value)}
                        className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Github / Portfolio URL</Label>
                      <Input
                        name="githubUrl"
                        defaultValue={formData.githubUrl}
                        onChange={(e) => updateFormField('githubUrl', e.target.value)}
                        className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl transition-all h-12"
                      />
                    </div>
                  </div>
                </div>

              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-4 mt-12 pt-8 border-t border-gray-100">
                <Button type="button" variant="ghost" className="text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-xl" onClick={() => window.location.reload()}>Clear Form</Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || !isFormValid}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white min-w-[160px] h-12 rounded-xl shadow-lg shadow-blue-500/20 font-medium tracking-wide transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? "Creating Profile..." : "Submit Application"}
                </Button>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - Documents & Skills */}
          <div className="lg:col-span-4 space-y-6">
            <div className="sticky top-28 space-y-6">
              {/* Cover Letter */}
              <div className="bg-white/70 backdrop-blur-xl p-6 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50">
                <Label className="text-sm font-bold text-gray-900 mb-4 block flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500" /> Cover Letter *
                </Label>
                <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white/50 focus-within:ring-4 focus-within:ring-blue-500/10 focus-within:border-blue-500 transition-all">
                  <div className="flex items-center gap-1 p-2 border-b border-gray-200/50 bg-gray-50/50">
                    <Button variant="ghost" size="sm" type="button" className="h-8 w-8 p-0 rounded-lg hover:bg-white hover:shadow-sm"><Bold className="w-3.5 h-3.5 text-gray-500" /></Button>
                    <Button variant="ghost" size="sm" type="button" className="h-8 w-8 p-0 rounded-lg hover:bg-white hover:shadow-sm"><Italic className="w-3.5 h-3.5 text-gray-500" /></Button>
                    <div className="w-px h-4 bg-gray-200 mx-1"></div>
                    <Button variant="ghost" size="sm" type="button" className="h-8 w-8 p-0 rounded-lg hover:bg-white hover:shadow-sm"><List className="w-3.5 h-3.5 text-gray-500" /></Button>
                    <Button variant="ghost" size="sm" type="button" className="h-8 w-8 p-0 rounded-lg hover:bg-white hover:shadow-sm"><AlignLeft className="w-3.5 h-3.5 text-gray-500" /></Button>
                  </div>
                  <Textarea
                    name="coverLetter"
                    required
                    defaultValue={formData.coverLetter}
                    onChange={(e) => updateFormField('coverLetter', e.target.value)}
                    className="border-0 focus-visible:ring-0 min-h-[140px] resize-none p-4 bg-transparent"
                    placeholder="Write or paste your cover letter here..."
                  />
                </div>
              </div>

              {/* Resume Upload */}
              <div className="bg-white/70 backdrop-blur-xl p-6 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50">
                <Label className="text-sm font-bold text-gray-900 mb-2 block flex items-center gap-2">
                  <Paperclip className="w-4 h-4 text-purple-500" /> Resume / CV
                </Label>

                <div className="border-2 border-dashed border-blue-200/50 rounded-2xl p-8 transition-all hover:border-blue-400/50 hover:bg-blue-50/30 text-center relative pointer-events-none group flex flex-col items-center justify-center bg-gray-50/30 min-h-[220px]">
                  <Input
                    id="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer pointer-events-auto z-10"
                    onChange={handleFileChange}
                  />
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-sm border border-blue-100 flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                    {fileName ? <CheckCircle className="w-8 h-8 text-green-500" /> : <Upload className="w-8 h-8 text-blue-600" />}
                  </div>
                  {fileName ? (
                    <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm font-medium break-all max-w-full relative z-20">
                      {fileName}
                    </div>
                  ) : (
                    <>
                      <p className="font-bold text-gray-900 mb-1">Click to upload or drag and drop</p>
                      <p className="text-sm text-gray-400 mb-4">Maximum file size 10MB (PDF, DOCX)</p>
                      <span className="inline-flex items-center justify-center px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 shadow-sm group-hover:bg-blue-50 group-hover:text-blue-600 group-hover:border-blue-200 transition-colors">
                        Select File
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Skills */}
              <div className="bg-white/70 backdrop-blur-xl p-6 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 space-y-5">
                <Label className="text-sm font-bold text-gray-900 block flex items-center gap-2">
                  <Bold className="w-4 h-4 text-orange-500" /> Skills & Expertise *
                </Label>

                <div className="space-y-3">
                  <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Manual Entry (Comma Separated) *</Label>
                  <Input
                    name="skills"
                    required
                    defaultValue={formData.skills}
                    onChange={(e) => updateFormField('skills', e.target.value)}
                    className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl h-11"
                    placeholder="React, Node.js, TypeScript..."
                  />
                </div>

                <div className="space-y-3">
                  <Label className="uppercase text-[11px] font-bold text-gray-400 tracking-widest pl-1">Extracted Keywords</Label>
                  <div className="bg-gray-50/50 border border-gray-200/50 p-4 rounded-xl min-h-[60px] text-sm text-gray-400 flex items-center justify-center italic">
                    Keywords from resume will appear here...
                  </div>
                </div>


              </div>

              <div className="flex items-start gap-3 px-2">
                <input type="checkbox" id="terms" required className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <Label htmlFor="terms" className="text-xs text-gray-500 leading-normal cursor-pointer">
                  I agree to the <a href="#" className="underline decoration-gray-300 hover:decoration-blue-500 hover:text-blue-600">Terms & Conditions</a> and <a href="#" className="underline decoration-gray-300 hover:decoration-blue-500 hover:text-blue-600">Privacy Policy</a>
                </Label>
              </div>
            </div>
          </div>

          {/* <Navigation /> and <Footer /> closing div removed as ReplacementChunk handles content replacment */}
        </form>
      </div>
      <Footer />
    </div>
  )
}

export default JobApplication
